import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { 
  getCampaigns, 
  createCampaign, 
  updateCampaign, 
  deleteCampaign,
  addKolToCampaign,
  removeKolFromCampaign
} from '../../services/emailCampaignService';
import { Kol } from './kolSlice';

export interface EmailCampaign {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'active' | 'completed' | 'canceled';
  startDate: string;
  endDate: string | null;
  kols: CampaignKol[];
  createdAt: string;
  updatedAt: string;
}

export interface CampaignKol {
  id: string;
  kolId: string;
  campaignId: string;
  kol: Kol;
  dateTime: string;
  numberOfPeople: number;
  treatment: string;
  status: 'pending' | 'confirmed' | 'canceled';
  createdAt: string;
  updatedAt: string;
}

interface EmailCampaignState {
  campaigns: EmailCampaign[];
  selectedCampaign: EmailCampaign | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: EmailCampaignState = {
  campaigns: [],
  selectedCampaign: null,
  isLoading: false,
  error: null,
};

export const fetchCampaigns = createAsyncThunk('emailCampaign/fetchCampaigns', async (_, { rejectWithValue }) => {
  try {
    const response = await getCampaigns();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch email campaigns');
  }
});

export const addCampaign = createAsyncThunk(
  'emailCampaign/addCampaign', 
  async (newCampaign: Omit<EmailCampaign, 'id' | 'kols' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      const response = await createCampaign(newCampaign);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create email campaign');
    }
  }
);

export const editCampaign = createAsyncThunk(
  'emailCampaign/editCampaign', 
  async ({ id, data }: { id: string; data: Partial<EmailCampaign> }, { rejectWithValue }) => {
    try {
      const response = await updateCampaign(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update email campaign');
    }
  }
);

export const removeCampaign = createAsyncThunk('emailCampaign/removeCampaign', async (id: string, { rejectWithValue }) => {
  try {
    await deleteCampaign(id);
    return id;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete email campaign');
  }
});

export const addKolToCampaignAction = createAsyncThunk(
  'emailCampaign/addKolToCampaign',
  async ({ 
    campaignId, 
    kolData 
  }: { 
    campaignId: string; 
    kolData: { 
      kolId: string; 
      dateTime: string; 
      numberOfPeople: number; 
      treatment: string; 
    } 
  }, { rejectWithValue }) => {
    try {
      const response = await addKolToCampaign(campaignId, kolData);
      return { campaignId, kolData: response };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add KOL to campaign');
    }
  }
);

export const removeKolFromCampaignAction = createAsyncThunk(
  'emailCampaign/removeKolFromCampaign',
  async ({ campaignId, kolCampaignId }: { campaignId: string; kolCampaignId: string }, { rejectWithValue }) => {
    try {
      await removeKolFromCampaign(campaignId, kolCampaignId);
      return { campaignId, kolCampaignId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove KOL from campaign');
    }
  }
);

const emailCampaignSlice = createSlice({
  name: 'emailCampaign',
  initialState,
  reducers: {
    setSelectedCampaign: (state, action: PayloadAction<EmailCampaign | null>) => {
      state.selectedCampaign = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCampaigns.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.isLoading = false;
        state.campaigns = action.payload;
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(addCampaign.fulfilled, (state, action) => {
        state.campaigns.push(action.payload);
        message.success('Email campaign created successfully');
      })
      .addCase(addCampaign.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(editCampaign.fulfilled, (state, action) => {
        const index = state.campaigns.findIndex(campaign => campaign.id === action.payload.id);
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
        message.success('Email campaign updated successfully');
      })
      .addCase(editCampaign.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(removeCampaign.fulfilled, (state, action) => {
        state.campaigns = state.campaigns.filter(campaign => campaign.id !== action.payload);
        message.success('Email campaign deleted successfully');
      })
      .addCase(removeCampaign.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(addKolToCampaignAction.fulfilled, (state, action) => {
        const { campaignId, kolData } = action.payload;
        const campaignIndex = state.campaigns.findIndex(c => c.id === campaignId);
        
        if (campaignIndex !== -1) {
          state.campaigns[campaignIndex].kols.push(kolData);
        }
        
        if (state.selectedCampaign?.id === campaignId) {
          state.selectedCampaign.kols.push(kolData);
        }
        
        message.success('KOL added to campaign successfully');
      })
      .addCase(addKolToCampaignAction.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      })
      .addCase(removeKolFromCampaignAction.fulfilled, (state, action) => {
        const { campaignId, kolCampaignId } = action.payload;
        const campaignIndex = state.campaigns.findIndex(c => c.id === campaignId);
        
        if (campaignIndex !== -1) {
          state.campaigns[campaignIndex].kols = state.campaigns[campaignIndex].kols.filter(
            k => k.id !== kolCampaignId
          );
        }
        
        if (state.selectedCampaign?.id === campaignId) {
          state.selectedCampaign.kols = state.selectedCampaign.kols.filter(
            k => k.id !== kolCampaignId
          );
        }
        
        message.success('KOL removed from campaign successfully');
      })
      .addCase(removeKolFromCampaignAction.rejected, (state, action) => {
        state.error = action.payload as string;
        message.error(action.payload as string);
      });
  },
});

export const { setSelectedCampaign } = emailCampaignSlice.actions;
export default emailCampaignSlice.reducer; 